import moment from 'moment';
import React from 'react';
import If from 'src/components/Shared/If/If';
import Phone from 'src/components/Shared/Phone/Phone';
import { default as config } from 'src/config/config';
import {
  APP_TOLL_FREE_NUMBER,
  FundNoteAutoExchange,
  FundNoteAutoRMD,
  TOLL_FREE_NUMBER,
} from 'src/settings/settings';
import { appendBasePath } from 'src/utils/nav.utils';
import ESAPlanFundNoteTip from './ESAPlanFundNoteTip';
import FundNote from './FundNote';
import './FundNote.module.scss';
import PendingTransactionFundNoteTip from './PendingTransactionFundNoteTip';
import RecentlyRedeemedFundNoteTip from './RecentlyRedeemedFundNoteTip';
import RestrictedCountryFundNoteTip from './RestrictedCountryFundNoteTip';
import { FundNoteListProps } from './types';

export const AutoExchangeFundNotes: React.VFC<FundNoteListProps> = ({
  fund,
}) => {
  const updateLinkAMC = `${config.url}${appendBasePath('/automatic')}`;
  const updateLinkRMD = `${config.url}/usis/rmd-tool`;
  const buyLink = `${config.url}${appendBasePath('/buy')}`;
  return (
    <React.Fragment>
      <If test={fund.notes[FundNoteAutoExchange.IRA_AUTOMAX]}>
        <FundNote
          labelSubText=""
          labelText="IRA Automax"
          tipHeader="IRA Automax"
          tipMessage={
            <div className="exclude">
              An IRA Automax exists on this fund. To make changes,{' '}
              <a className="exclude" href={updateLinkAMC}>
                edit or delete
              </a>{' '}
              your existing plan.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.EXISTING_RMD]}>
        <FundNote
          labelSubText=""
          labelText="Auto-RMD in Progress"
          tipHeader="Auto-RMD in Progress"
          tipMessage={
            <div className="exclude">
              You are unable to set-up a systematic out of this account due to
              an existing required minimum distribution. To edit or delete your
              Auto-RMD,{' '}
              <a
                className="exclude college-branded-link"
                href={`${config.url}/usis/rmd-tool`}
              >
                click here
              </a>
              .
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.AUTO_BUY]}>
        <FundNote
          labelSubText=""
          labelText={'Automatic Buy'}
          tipHeader={'Automatic Buy'}
          tipMessage={
            <div className="exclude">
              An Automatic Buy already exists on this fund. To make changes,
              please{' '}
              <a className="exclude" href={updateLinkAMC}>
                edit or delete
              </a>{' '}
              your existing plan.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoRMD.AUTO_RMD]}>
        <FundNote
          labelSubText=""
          labelText="Auto RMD"
          tipHeader="Auto RMD"
          tipMessage={
            <div className="exclude">
              A custom Auto RMD exists on this fund. To make changes,{' '}
              <a className="exclude college-branded-link" href={updateLinkRMD}>
                edit or delete
              </a>{' '}
              your existing plan to proportionate.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.AUTO_SELL]}>
        <FundNote
          labelSubText=""
          labelText="Automatic Sell"
          tipHeader="Automatic Sell"
          tipMessage={
            <div className="exclude">
              An Automatic Sell exists on this fund. To make changes,{' '}
              <a className="exclude" href={updateLinkAMC}>
                edit or delete
              </a>{' '}
              your existing plan.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.AUTO_EXCHANGE]}>
        <FundNote
          labelSubText=""
          labelText="Automatic Exchange"
          tipHeader="Automatic Exchange"
          tipMessage={
            <div className="exclude">
              An Automatic Exchange exists on this fund. To make changes,{' '}
              <a className="exclude" href={updateLinkAMC}>
                edit or delete
              </a>{' '}
              your existing plan.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.AUTO_EXCHANGE_TO]}>
        <FundNote
          labelSubText=""
          labelText="Automatic Exchange Unavailable"
          tipHeader="Automatic Exchange Unavailable"
          tipMessage={
            <div className="exclude">
              An Automatic Exchange currently exists on this fund. To make
              changes,{' '}
              <a className="exclude college-branded-link" href={updateLinkAMC}>
                edit or delete
              </a>{' '}
              your existing plan.
            </div>
          }
        />
      </If>
      <If
        test={
          (fund.notes[FundNoteAutoExchange.STOP_PURCHASE] ||
            fund.notes[FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_ALL]) &&
          fund.productType === 'ManagedAccount'
        }
      >
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage={
            <div className="exclude">
              Your account has a feature on it that restricts exchanges. Call{' '}
              <Phone phoneNumber={APP_TOLL_FREE_NUMBER} /> to discuss options
            </div>
          }
        />
      </If>
      <If
        test={
          fund.notes[FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_CHECK] ||
          fund.notes[FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_PURCHASE] ||
          fund.notes[FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_EXCHANGE]
        }
      >
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage={
            fund.productType === 'ManagedAccount' ? (
              <div className="exclude">
                Your account has a feature on it that restricts exchanges. Call{' '}
                <Phone phoneNumber={APP_TOLL_FREE_NUMBER} /> to discuss options
              </div>
            ) : (
              'The exchange feature is currently not available for this account. In order to add this feature, please complete the Shareholder Services Form for a taxable account and the IRA Shareholder Services Form for IRA accounts.'
            )
          }
        />
      </If>
      <If
        test={fund.notes[FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_BALANCE]}
      >
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="You are unable to exchange out of this account due to zero shares. An exchange from another account into this account is still allowed."
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.RETURN_MAIL_VERIFY_ADDRESS]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="We have received undeliverable mail from the Post Office for your address on record. Please verify your address on record is correct and update if necessary."
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.PURCHASED_BLOCKED]}>
        <FundNote
          labelSubText={`Available on or after ${moment(
            fund.purchaseBlockRemovalDate
          ).format('MM/DD')}.`}
          labelText="Recently Redeemed"
          tipHeader="Recently Redeemed"
          tipMessage={<RecentlyRedeemedFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.ONLINE_PURCHASE_RESTRICTED]}>
        <FundNote
          labelSubText=""
          labelText="Unavailable Online"
          tipHeader="Unavailable Online"
          tipMessage={
            <div className="exclude">
              Online/phone redemption disabled. Call{' '}
              <Phone phoneNumber={TOLL_FREE_NUMBER} /> to enable redemptions.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.CLOSED_NEW]}>
        <FundNote labelSubText="" labelText="Closed to New Investors" />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.FID_NON_FID]}>
        <FundNote
          labelSubText="Clients are not able to exchange between fiduciary and non-fiduciary accounts."
          labelText="Unavailable for Exchange"
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.TAXABLE_TO_IRA]}>
        <FundNote
          labelSubText=""
          labelText="FIX COPY HERE - CAN'T EXCHANGE TAXABLE TO IRA"
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.CLOSED_FUND]}>
        <FundNote
          labelSubText=""
          labelText="Closed Fund"
          tipHeader="Closed Fund"
          tipMessage={
            <div className="exclude">
              All shares have been redeemed or exchanged in a previous
              transaction. Fund must be re-opened with new account minimums
              before an Automatic Exchange can be established. Fund can be
              re-opened by making a one-time{' '}
              <a className="exclude" href={buyLink}>
                {' '}
                Mutual Fund Buy
              </a>
              .
            </div>
          }
        />
      </If>
      <If
        test={fund.notes[FundNoteAutoExchange.MONEY_MARKET_EVENT_GATE_ACTIVE]}
      >
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
            />
          }
          labelText="Redemption Block"
        />
      </If>
      <If
        test={fund.notes[FundNoteAutoExchange.MONEY_MARKET_EVENT_GATE_REMOVED]}
      >
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
            />
          }
          labelText="Redemption Block Removed"
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.MONEY_MARKET_EVENT_FEE_ACTIVE]}>
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
            />
          }
          labelText="Liquidity Fee"
        />
      </If>
      <If
        test={fund.notes[FundNoteAutoExchange.MONEY_MARKET_EVENT_FEE_REMOVED]}
      >
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
            />
          }
          labelText="Liquidity Fee Removed"
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.ESA_PLAN]}>
        <FundNote
          labelSubText=""
          labelText="ESA Plan"
          tipHeader="ESA Plan"
          tipMessage={<ESAPlanFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.RETURN_MAIL_VERIFY_ADDRESS]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="We have received undeliverable mail from the Post Office for your address on record. Please verify your address on record is correct and update if necessary."
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.PENALTY_WITHHOLDING]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="Restrictive feature regarding tax withholding on your account does not allow exchanges online."
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.FROM_ZERO]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="You are unable to exchange out of this account due to zero shares. An exchange from another account into this account is still allowed."
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.COUNTRY_RESTRICTED]}>
        <FundNote
          labelSubText="Requires Permanent US Address."
          labelText="Restricted"
          tipHeader="Restricted Country"
          tipMessage={<RestrictedCountryFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.STOP_TRANSFER]}>
        <FundNote
          labelSubText=""
          labelText="Stop Transfer"
          tipHeader="Stop Transfer"
          tipMessage="Your account has a feature on it that restricts exchanges."
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.PENDING]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.PENDING_TRANSACTION]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip allFunds={true} />}
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.UNAVAILABLE]}>
        <FundNote labelText="Exchange Unavailable" />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.INHERITED_FUND]}>
        <FundNote
          labelSubText=""
          labelText="Inherited IRA"
          tipHeader="Inherited IRA"
          tipMessage={
            <div className="exclude">
              Inherited IRA accounts are not eligible for online transactions.
              Please
              <a
                className="exclude"
                href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                rel="noreferrer"
                target="_blank"
              >
                contact us
              </a>{' '}
              to redeem from your Inherited IRA.
            </div>
          }
        />
      </If>
      <If
        test={
          fund.notes[FundNoteAutoExchange.IRA_MATCHING] ||
          fund.notes[FundNoteAutoExchange.NON_IRA_MATCHING]
        }
      >
        <FundNote
          labelSubText=""
          labelText="Different Ownership"
          tipHeader="Different Ownership"
          tipMessage={
            <div className="exclude">
              This fund is not available for exchange based on the fund&apos;s
              registration and/or ownership.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteAutoExchange.NONRETIREMENT_TO_RETIREMENT]}>
        <FundNote
          labelSubText=""
          labelText="Unavailable for exchange"
          tipHeader="Unavailable for exchange"
          tipMessage={
            <div className="exclude">
              An automatic exchange from a non-retirement account to a
              retirement account is not allowed.
            </div>
          }
        />
      </If>
      {/* <If test={fund.notes[FundNoteAutoExchange.NOT_ELIGIBLE]}>
        <FundNote
          labelSubText={''}
          labelText="Ineligible Exchange Account"
          tipHeader="Ineligible Exchange Account"
          tipMessage={
            <div className="exclude">
              Exchanges among your selected investment(s) and this investment
              are ineligible due to differences in the account details.{' '}
              <a
                className="exclude no-break"
                href={`${config.url}/personal-investing/help/transactions.html`}
              >
                Learn why.
              </a>
            </div>
          }
        />
      </If> */}
    </React.Fragment>
  );
};
